'use client';

import './feature-buttons.scss';

import type { ElementType } from 'react';
import { useState } from 'react';
import { z } from 'zod';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { IconNames } from '@sola/schema/lib/icons/icon-names.ts';
import { IconNamesSafe } from '@sola/schema/lib/icons/icon-names.ts';
import type { Link as LinkSchema } from '@sola/schema/lib/schema.ts';
import { FlexibleContentProps } from '@sola/schema/lib/schema.ts';
import { isAnchorLink } from '@sola/utils/url.ts';
import { isPlaylistLink, isVideoLink } from '@sola/utils/video.ts';

import { handleAnchorClick } from '../../utils/anchor.ts';
import { Lightbox, LightboxTrigger } from '../lightbox/lightbox.tsx';

enum DisplayAs {
  Links = 'links',
  Buttons = 'buttons',
}

interface CTAProps {
  cta: LinkSchema;
  variant?: 'primary' | 'secondary' | 'tertiary' | 'link';
  iconName?: IconNames;
  iconPlacement: 'before' | 'after';
  buttonSize: 'sm' | 'md' | 'lg' | 'xl';
  displayAs: DisplayAs;
  onVideoClick?: (index: number) => void;
  videoIndex?: number;
}

export const FeatureButtonsProps = FlexibleContentProps.extend({
  buttonSize: z.enum(['sm', 'md', 'lg', 'xl']).optional(),
  primaryCTAVariant: z.enum(['primary', 'secondary', 'tertiary']).optional(),
  primaryCTAIconName: IconNamesSafe.optional(),
  primaryCTAIconPlacement: z.enum(['before', 'after']).optional(),
  secondaryCTAVariant: z.enum(['primary', 'secondary', 'tertiary']).optional(),
  secondaryCTAIconName: IconNamesSafe.optional(),
  secondaryCTAIconPlacement: z.enum(['before', 'after']).optional(),
  tertiaryCTAVariant: z.enum(['tertiary', 'link']).optional(),
  tertiaryCTAIconName: IconNamesSafe.optional(),
  tertiaryCTAIconPlacement: z.enum(['before', 'after']).optional(),
  variant: z.enum(['default', 'stacked', 'grow', 'noWrap']).optional(),
  alignment: z.enum(['left', 'right', 'center', 'default']).optional(),
  isLinks: z.boolean().optional(),
  inline: z.boolean().optional(),
  className: z.string().optional(),
});
export type FeatureButtonsProps = z.infer<typeof FeatureButtonsProps>;

const buttonSpacings = {
  sm: {
    button: 'gap-snug-lg',
    link: 'gap-comfy-sm',
  },
  md: {
    button: 'gap-snug-lg',
    link: 'gap-comfy-md',
  },
  lg: {
    button: 'gap-snug-lg',
    link: 'gap-comfy-lg',
  },
  xl: {
    button: 'gap-snug-lg',
    link: 'gap-comfy-lg',
  },
};

const getButtonSpacing = (
  size: FeatureButtonsProps['buttonSize'] = 'md',
  variant: FeatureButtonsProps['variant'] = 'default',
) => {
  const baseSpacings = {
    sm: { button: 'gap-snug-lg', link: 'gap-comfy-sm' },
    md: { button: 'gap-snug-lg', link: 'gap-comfy-md' },
    lg: { button: 'gap-snug-lg', link: 'gap-comfy-lg' },
    xl: { button: 'gap-snug-lg', link: 'gap-comfy-lg' },
  };

  const variantOverrides: Partial<
    Record<
      Exclude<FeatureButtonsProps['variant'], undefined>,
      Partial<(typeof baseSpacings)['md']>
    >
  > = {
    grow: { button: 'gap-comfy-xs @sm:gap-snug-lg' },
    stacked: { button: 'gap-comfy-xs @sm:gap-snug-lg' },
  };

  const spacing = baseSpacings[size];
  const overrides = variantOverrides[variant] ?? {};

  return {
    button: overrides.button ?? spacing.button,
    link: overrides.link ?? spacing.link,
  };
};

const variantClasses = {
  default:
    'flex flex-row items-center @sm:items-center @sm:flex-row @sm:justify-center flex-wrap',
  stacked: 'flex flex-col',
  grow: 'flex flex-col items-center @sm:items-center @sm:flex-row @sm:justify-center flex-wrap',
  noWrap: 'flex flex-col items-center @sm:flex-row @sm:justify-center',
};

const renderCTA = ({
  cta,
  variant,
  iconName,
  iconPlacement,
  buttonSize,
  displayAs,
  onVideoClick,
  videoIndex,
}: CTAProps) => {
  const shouldRenderAsLink =
    displayAs === DisplayAs.Links || variant === 'link';
  const isVideo = isVideoLink(cta.link);
  const isPlaylist = isPlaylistLink(cta.link);
  const linkTarget = isPlaylist ? '_blank' : cta.target;
  const linkRel = isPlaylist ? 'noopener noreferrer' : undefined;
  if (shouldRenderAsLink) {
    const linkContent = (
      <LinkStyle
        size={buttonSize}
        iconAfter={
          iconPlacement === 'after' ? iconName || 'arrow-right' : undefined
        }
        iconBefore={iconPlacement === 'before' ? iconName : undefined}
        noPadding
        noUnderline
        inline
      >
        {isVideo ? (
          <span>{cta.title}</span>
        ) : (
          <Link
            download={cta.isDownload}
            href={cta.link}
            target={linkTarget}
            rel={linkRel}
            scroll={!isAnchorLink(cta.link)}
            onClick={(e) => handleAnchorClick(e, cta.link)}
          >
            {cta.title}
          </Link>
        )}
      </LinkStyle>
    );

    if (isVideo) {
      return (
        <LightboxTrigger
          stopPropagation
          onClick={() => onVideoClick?.(videoIndex || 0)}
        >
          {linkContent}
        </LightboxTrigger>
      );
    }

    return linkContent;
  }

  const buttonContent = (
    <ButtonStyle variant={variant} size={buttonSize}>
      {isVideo ? (
        <div>
          <ArkIcon iconName="play-circle" />
          {cta.title}
        </div>
      ) : (
        <Link
          download={cta.isDownload}
          href={cta.link}
          target={linkTarget}
          rel={linkRel}
          scroll={!isAnchorLink(cta.link)}
          onClick={(e) => handleAnchorClick(e, cta.link)}
          title={cta.title}
        >
          {iconName && iconPlacement === 'before' && (
            <ArkIcon iconName={iconName} />
          )}
          {cta.title}
          {iconName && iconPlacement === 'after' && (
            <ArkIcon iconName={iconName} />
          )}
        </Link>
      )}
    </ButtonStyle>
  );

  if (isVideo) {
    return (
      <LightboxTrigger
        onClick={() => onVideoClick?.(videoIndex || 0)}
        stopPropagation
      >
        {buttonContent}
      </LightboxTrigger>
    );
  }

  return buttonContent;
};

export const FeatureButtons: React.FC<
  FeatureButtonsProps & {
    children?: React.ReactNode | React.ReactNode[];
  }
> = ({
  buttonSize = 'lg',
  primaryCTA,
  primaryCTAVariant = 'primary',
  primaryCTAIconName,
  primaryCTAIconPlacement = 'before',
  secondaryCTA,
  secondaryCTAVariant = 'secondary',
  secondaryCTAIconName,
  secondaryCTAIconPlacement = 'before',
  tertiaryCTA,
  tertiaryCTAVariant = 'tertiary',
  tertiaryCTAIconName = 'arrow-right',
  tertiaryCTAIconPlacement = 'after',
  variant = 'default',
  alignment = 'default',
  isLinks = false,
  className,
  children,
}) => {
  const [startIndex, setStartIndex] = useState(0);
  const displayAs = isLinks ? DisplayAs.Links : DisplayAs.Buttons;

  const tertiaryIsLink =
    tertiaryCTA &&
    (displayAs === DisplayAs.Links ||
      tertiaryCTAVariant === 'link' ||
      (!primaryCTA && !secondaryCTA) ||
      variant === 'stacked');

  const buttonSpacing =
    isLinks || tertiaryIsLink
      ? getButtonSpacing(buttonSize, variant).link
      : getButtonSpacing(buttonSize, variant).button;

  const lightboxItems = [];
  let videoIndex = 0;

  if (primaryCTA && isVideoLink(primaryCTA.link)) {
    lightboxItems.push({ media: { video: { url: primaryCTA.link } } });
  }
  if (secondaryCTA && isVideoLink(secondaryCTA.link)) {
    lightboxItems.push({ media: { video: { url: secondaryCTA.link } } });
  }
  if (tertiaryCTA && isVideoLink(tertiaryCTA.link)) {
    lightboxItems.push({ media: { video: { url: tertiaryCTA.link } } });
  }

  const handleVideoClick = (index: number) => {
    setStartIndex(index);
  };

  const Comp = lightboxItems.length > 0 ? Lightbox : ('div' as ElementType);

  return (
    <Comp
      {...(lightboxItems.length > 0 && { items: lightboxItems, startIndex })}
    >
      <div
        className={cn(
          `FeatureButtons FeatureButtons--${variant} alignment-${alignment}`,
          buttonSpacing,
          variantClasses[variant],
          primaryCTAVariant === 'tertiary' && 'FeatureButtons--stacked',
          tertiaryIsLink && buttonSize === 'lg' && 'pt-snug-lg',
          className,
        )}
      >
        {primaryCTA &&
          renderCTA({
            cta: primaryCTA,
            variant: primaryCTAVariant,
            iconName: primaryCTA.icon
              ? IconNamesSafe.parse(primaryCTA.icon)
              : primaryCTAIconName,
            iconPlacement: primaryCTA.icon ? 'before' : primaryCTAIconPlacement,
            buttonSize,
            displayAs,
            onVideoClick: handleVideoClick,
            videoIndex: isVideoLink(primaryCTA.link) ? videoIndex++ : undefined,
          })}
        {secondaryCTA &&
          renderCTA({
            cta: secondaryCTA,
            variant: secondaryCTAVariant,
            iconName: secondaryCTA.icon
              ? IconNamesSafe.parse(secondaryCTA.icon)
              : secondaryCTAIconName,
            iconPlacement: secondaryCTA.icon
              ? 'before'
              : secondaryCTAIconPlacement,
            buttonSize,
            displayAs,
            onVideoClick: handleVideoClick,
            videoIndex: isVideoLink(secondaryCTA.link)
              ? videoIndex++
              : undefined,
          })}
        {tertiaryCTA &&
          renderCTA({
            cta: tertiaryCTA,
            variant: tertiaryIsLink ? 'link' : tertiaryCTAVariant,
            iconName: tertiaryCTA.icon
              ? IconNamesSafe.parse(tertiaryCTA.icon)
              : tertiaryCTAIconName,
            iconPlacement: tertiaryCTA.icon
              ? 'before'
              : tertiaryCTAIconPlacement,
            buttonSize,
            displayAs,
            onVideoClick: handleVideoClick,
            videoIndex: isVideoLink(tertiaryCTA.link)
              ? videoIndex++
              : undefined,
          })}
        {children}
      </div>
    </Comp>
  );
};
